/**
 * Media Library (Médiathèque) — Admin screen for S6.1.
 * List, upload, delete, attribution (edit usage/owner via PATCH /api/v1/media/:id).
 * Filters: type, usage, ownerId, folder (tree sidebar). Folder CRUD for admins.
 */
import React, { useState, useEffect, useCallback } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
  Button,
  Row,
  Col,
} from "../../../components/Component";
import { FormGroup, Label, Input, Spinner, Badge } from "reactstrap";
import http from "../../../utils/http-common";
import swal from "sweetalert";
import { API_BASE_URL } from "../../../utils/Utils";
import { isAbortError } from "../../../utils/asyncRequestGuards";

function abortSignalFromArg(maybe) {
  return maybe && typeof maybe.abort === "function" ? maybe : undefined;
}

/** Flatten folder tree for dropdowns (id, name, depth for display). */
function flattenFolderTree(nodes, depth = 0) {
  if (!Array.isArray(nodes)) return [];
  return nodes.reduce((acc, node) => {
    acc.push({ ...node, _depth: depth });
    if (Array.isArray(node.children) && node.children.length) {
      acc.push(...flattenFolderTree(node.children, depth + 1));
    }
    return acc;
  }, []);
}

/** Collect all descendant ids from a tree node (to avoid moving folder into its own child). */
function getDescendantIds(node) {
  if (!node) return [];
  const ids = [];
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      if (child._id) ids.push(String(child._id));
      ids.push(...getDescendantIds(child).map(String));
    }
    return ids;
  }
  return ids;
}

const OWNER_TYPE_OPTIONS = [
  { value: "admin", label: "Admin / Global" },
  { value: "partner", label: "Partner" },
];

const PAGE_SIZE = 20;
const MAX_UPLOAD_FILES = 20;
const USAGE_OPTIONS = [
  { value: "", label: "All usages" },
  { value: "clinic", label: "Clinic" },
  { value: "partner", label: "Partner" },
  { value: "global", label: "Global" },
  { value: "banner", label: "Banner" },
  { value: "press_logos", label: "Press logos — home carousel" },
  { value: "logo", label: "Logo strip (CMS) — placement in Edit" },
];
const TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
  { value: "document", label: "Document" },
];

function buildMediaUrl(path) {
  if (!path) return "";
  const base = API_BASE_URL.replace(/\/$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}

const UNCategorized_ID = "none";
/** Default list = no folder filter (shows all media). "Uncategorized" is opt-in. */
const ALL_MEDIA_FOLDERS = "all";

const MediaLibrary = () => {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadAlt, setUploadAlt] = useState("");
  const [uploadUsage, setUploadUsage] = useState("global");
  const [uploadFolderId, setUploadFolderId] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [partners, setPartners] = useState([]);
  const [filters, setFilters] = useState({
    type: "",
    usage: "",
    ownerId: "",
    folderId: "",
  });
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    alt: "",
    usage: "global",
    ownerType: "admin",
    ownerId: "",
    folderId: "",
    cmsVisible: true,
    cmsOrder: 0,
    cmsLink: "",
    cmsPlacement: "",
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [folderTree, setFolderTree] = useState([]);
  const [folderTreeLoading, setFolderTreeLoading] = useState(true);
  const [folderTreeError, setFolderTreeError] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState(ALL_MEDIA_FOLDERS);

  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderParentId, setNewFolderParentId] = useState("");
  const [newFolderSaving, setNewFolderSaving] = useState(false);

  const [editFolderItem, setEditFolderItem] = useState(null);
  const [editFolderName, setEditFolderName] = useState("");
  const [editFolderParentId, setEditFolderParentId] = useState("");
  const [editFolderSaving, setEditFolderSaving] = useState(false);

  const fetchFolders = useCallback(async (maybeSignal) => {
    const signal = abortSignalFromArg(maybeSignal);
    setFolderTreeLoading(true);
    setFolderTreeError(null);
    const httpOpts = { signal, suppressInlineErrorSwal: true };
    const paramAttempts = [{ tree: 1 }, { tree: "1" }, { tree: true }, {}];
    try {
      let lastErr;
      for (const params of paramAttempts) {
        try {
          const res = await http.get("/api/v1/media-folders", { ...httpOpts, params });
          const data = res.data?.data ?? res.data;
          setFolderTree(Array.isArray(data) ? data : []);
          return;
        } catch (e) {
          lastErr = e;
          if (isAbortError(e)) return;
          const st = e.response?.status;
          if (st !== 400 && st !== 404) throw e;
        }
      }
      throw lastErr;
    } catch (err) {
      if (isAbortError(err)) return;
      const msg = err.response?.data?.error || err.message || "Failed to load folders";
      setFolderTreeError(msg);
      setFolderTree([]);
    } finally {
      if (!signal?.aborted) setFolderTreeLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    fetchFolders(ac.signal);
    return () => ac.abort();
  }, [fetchFolders]);

  const fetchList = useCallback(
    async (pageNum = 1, maybeSignal) => {
      const signal = abortSignalFromArg(maybeSignal);
      setLoading(true);
      try {
        const params = { page: pageNum, limit: PAGE_SIZE };
        if (filters.type) params.type = filters.type;
        if (filters.usage) params.usage = filters.usage;
        if (filters.ownerId) params.ownerId = filters.ownerId;
        if (selectedFolderId === UNCategorized_ID) {
          params.folderId = "none";
        } else if (selectedFolderId && selectedFolderId !== ALL_MEDIA_FOLDERS) {
          params.folderId = selectedFolderId;
        }
        const res = await http.get("/api/v1/media", { params, signal });
        const data = res.data?.data ?? res.data;
        setList(Array.isArray(data) ? data : []);
        setTotal(res.data?.total ?? 0);
        setPage(res.data?.page ?? pageNum);
        setPages(res.data?.pages ?? 1);
      } catch (err) {
        if (isAbortError(err)) return;
        const msg =
          err.response?.data?.error || err.message || "Failed to load media";
        swal("Error", msg, "error");
        setList([]);
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [filters.type, filters.usage, filters.ownerId, selectedFolderId]
  );

  useEffect(() => {
    const ac = new AbortController();
    fetchList(1, ac.signal);
    return () => ac.abort();
  }, [fetchList]);

  useEffect(() => {
    const ac = new AbortController();
    http
      .get("/api/v1/medical-partners", { params: { limit: 500 }, signal: ac.signal })
      .then((res) => {
        const raw = res.data?.data ?? res.data;
        setPartners(Array.isArray(raw) ? raw : []);
      })
      .catch((e) => {
        if (!isAbortError(e)) setPartners([]);
      });
    return () => ac.abort();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (fileList) => {
    if (!fileList?.length) return;
    const files = Array.from(fileList).slice(0, MAX_UPLOAD_FILES);
    setUploadFiles(files);
    if (files.length === 1) setUploadFile(files[0]);
    else setUploadFile(null);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const hasFiles = uploadFiles.length > 0 || uploadFile;
    if (!hasFiles) {
      swal("Missing file", "Please select one or more files to upload (max " + MAX_UPLOAD_FILES + ").", "warning");
      return;
    }
    setUploading(true);
    try {
      const total = uploadFiles.length || (uploadFile ? 1 : 0);
      const filesToUpload = uploadFiles.length > 0 ? uploadFiles : uploadFile ? [uploadFile] : [];

      if (filesToUpload.length === 1 && (uploadTitle.trim() || uploadAlt.trim())) {
        const formData = new FormData();
        formData.append("file", filesToUpload[0]);
        if (uploadTitle.trim()) formData.append("title", uploadTitle.trim());
        if (uploadAlt.trim()) formData.append("alt", uploadAlt.trim());
        formData.append("usage", uploadUsage);
        if (uploadFolderId) formData.append("folderId", uploadFolderId);
        await http.post("/api/v1/media", formData);
        swal("Success", "1 file added.", "success");
        setUploadTitle("");
        setUploadAlt("");
        setUploadFiles([]);
        setUploadFile(null);
        const multiInput = document.getElementById("media-library-file-input-multi");
        if (multiInput) multiInput.value = "";
        fetchList(page);
      } else {
        let successCount = 0;
        try {
          const formData = new FormData();
          filesToUpload.forEach((file) => formData.append("files", file));
          formData.append("usage", uploadUsage);
          if (uploadFolderId) formData.append("folderId", uploadFolderId);
          const res = await http.post("/api/v1/media/bulk", formData);
          const data = res.data?.data;
          successCount = Array.isArray(data) ? data.length : total;
        } catch (bulkErr) {
          const is404 = bulkErr.response?.status === 404;
          const is4xx = bulkErr.response?.status >= 400 && bulkErr.response?.status < 500;
          if (is404 || is4xx) {
            for (const file of filesToUpload) {
              try {
                const fd = new FormData();
                fd.append("file", file);
                fd.append("usage", uploadUsage);
                if (uploadFolderId) fd.append("folderId", uploadFolderId);
                await http.post("/api/v1/media", fd);
                successCount += 1;
              } catch (_) {}
            }
          } else {
            throw bulkErr;
          }
        }
        if (successCount === total) {
          swal("Success", `${successCount} file(s) added.`, "success");
        } else if (successCount > 0) {
          swal("Partial upload", `${successCount} of ${total} file(s) added.`, "warning");
        } else {
          swal("Upload failed", "No files were uploaded.", "error");
        }
      }
      setUploadFiles([]);
      setUploadFile(null);
      const multiInput = document.getElementById("media-library-file-input-multi");
      if (multiInput) multiInput.value = "";
      fetchList(page);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Upload failed";
      swal("Upload error", msg, "error");
    } finally {
      setUploading(false);
    }
  };

  const openEditModal = (item) => {
    setEditItem(item);
    const usage =
      item.usage === "press" ? "logo" : item.usage || "global";
    setEditForm({
      title: item.title || "",
      alt: item.alt || "",
      usage,
      ownerType: item.ownerType === "partner" ? "partner" : "admin",
      ownerId: item.ownerId ? String(item.ownerId) : "",
      folderId: item.folderId ? String(item.folderId) : "",
      cmsVisible: item.cmsVisible !== false && item.pressVisible !== false,
      cmsOrder:
        typeof item.cmsOrder === "number"
          ? item.cmsOrder
          : typeof item.pressOrder === "number"
            ? item.pressOrder
            : 0,
      cmsLink: item.cmsLink || item.pressLink || "",
      cmsPlacement:
        (item.cmsPlacement || (item.usage === "press" ? "home_press" : "")) ||
        "",
    });
  };

  const closeEditModal = () => {
    setEditItem(null);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editItem?._id) return;
    setSavingEdit(true);
    try {
      const payload = {
        title: editForm.title,
        alt: editForm.alt,
        usage: editForm.usage,
      };
      if (editForm.ownerType === "partner" && editForm.ownerId) {
        payload.ownerType = "partner";
        payload.ownerId = editForm.ownerId;
      } else {
        payload.ownerType = "admin";
        payload.ownerId = null;
      }
      payload.folderId =
        editForm.folderId === "" || editForm.folderId === "none"
          ? null
          : editForm.folderId;
      if (editForm.usage === "logo") {
        payload.cmsVisible = editForm.cmsVisible;
        payload.cmsOrder = Number(editForm.cmsOrder) || 0;
        const link = editForm.cmsLink.trim();
        payload.cmsLink = link || null;
        const place = editForm.cmsPlacement.trim();
        payload.cmsPlacement = place || null;
      }
      await http.patch(`/api/v1/media/${editItem._id}`, payload);
      swal("Saved", "Media updated.", "success");
      closeEditModal();
      fetchList(page);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Update failed";
      swal("Error", msg, "error");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await swal({
      title: "Delete media?",
      text: "This action cannot be undone.",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (!confirmed) return;
    try {
      await http.delete(`/api/v1/media/${id}`);
      swal("Deleted", "Media deleted.", "success");
      fetchList(page);
    } catch (err) {
      const msg =
        err.response?.data?.error || err.message || "Delete failed";
      swal("Error", msg, "error");
    }
  };

  const flatFolders = flattenFolderTree(folderTree);

  const handleNewFolderSubmit = async (e) => {
    e.preventDefault();
    const name = newFolderName.trim();
    if (!name) {
      swal("Validation", "Folder name is required.", "warning");
      return;
    }
    setNewFolderSaving(true);
    try {
      const payload = { name };
      if (newFolderParentId) payload.parentId = newFolderParentId;
      await http.post("/api/v1/media-folders", payload);
      setNewFolderOpen(false);
      setNewFolderName("");
      setNewFolderParentId("");
      await fetchFolders();
      fetchList(1);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Failed to create folder";
      swal("Error", msg, "error");
    } finally {
      setNewFolderSaving(false);
    }
  };

  const openEditFolder = (folder) => {
    setEditFolderItem(folder);
    setEditFolderName(folder.name || "");
    setEditFolderParentId(folder.parentId ? String(folder.parentId) : "");
  };

  const closeEditFolder = () => {
    setEditFolderItem(null);
  };

  const handleEditFolderSubmit = async (e) => {
    e.preventDefault();
    if (!editFolderItem?._id) return;
    const name = editFolderName.trim();
    if (!name) {
      swal("Validation", "Folder name is required.", "warning");
      return;
    }
    setEditFolderSaving(true);
    try {
      const payload = { name };
      if (editFolderParentId) payload.parentId = editFolderParentId; else payload.parentId = null;
      await http.patch(`/api/v1/media-folders/${editFolderItem._id}`, payload);
      closeEditFolder();
      await fetchFolders();
      if (selectedFolderId === editFolderItem._id) fetchList(page);
      else fetchList(1);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Failed to update folder";
      swal("Error", msg, "error");
    } finally {
      setEditFolderSaving(false);
    }
  };

  const handleDeleteFolder = async (folder) => {
    const confirmed = await swal({
      title: "Delete folder?",
      text: "Media in this folder will become uncategorized. This cannot be undone.",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (!confirmed) return;
    try {
      await http.delete(`/api/v1/media-folders/${folder._id}`);
      await fetchFolders();
      if (selectedFolderId === folder._id) {
        setSelectedFolderId(UNCategorized_ID);
        fetchList(1);
      } else {
        fetchList(page);
      }
      swal("Deleted", "Folder deleted.", "success");
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Delete failed";
      swal("Error", msg, "error");
    }
  };

  const renderFolderNode = (node, depth = 0) => {
    const id = node._id;
    const isSelected = selectedFolderId === id;
    return (
      <div key={id} style={{ marginLeft: depth * 12 }}>
        <div
          className={`d-flex align-items-center justify-content-between py-1 px-2 rounded ${isSelected ? "bg-primary text-white" : ""}`}
          style={{ cursor: "pointer" }}
        >
          <span
            className="flex-grow-1 text-truncate"
            onClick={() => setSelectedFolderId(id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setSelectedFolderId(id);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`Select folder ${node.name}`}
          >
            {node.name || "Unnamed"}
          </span>
          <div className="d-flex flex-g-1" onClick={(e) => e.stopPropagation()}>
            <Button
              size="sm"
              color={isSelected ? "light" : "primary"}
              outline
              className="p-1"
              aria-label={`Edit folder ${node.name}`}
              onClick={() => openEditFolder(node)}
            >
              Edit
            </Button>
            <Button
              size="sm"
              color="danger"
              outline
              className="p-1"
              aria-label={`Delete folder ${node.name}`}
              onClick={() => handleDeleteFolder(node)}
            >
              Delete
            </Button>
          </div>
        </div>
        {Array.isArray(node.children) &&
          node.children.map((child) => renderFolderNode(child, depth + 1))}
      </div>
    );
  };

  const thumbnailOrPreviewUrl = (item) => {
    if (item?.thumbnailUrl) return buildMediaUrl(item.thumbnailUrl);
    if (item?.type === "image" && item?.fileUrl) return buildMediaUrl(item.fileUrl);
    return null;
  };

  return (
    <>
      <Head title="Media library" />
      <Content>
        <Block size="lg">
          <BlockHead>
            <BlockHeadContent>
              <BlockTitle tag="h4">Media library</BlockTitle>
              <BlockDes className="text-soft">
                Your photos and videos in one place. Upload once, then use them on your clinic page or campaigns.
              </BlockDes>
            </BlockHeadContent>
          </BlockHead>

          <Row className="mb-4">
            <Col md="12">
              <form onSubmit={handleUpload}>
                <Row className="g-3">
                  <Col md="12">
                    <FormGroup>
                      <Label>Add photos or videos</Label>
                      <div
                        className={`border rounded p-3 text-center ${dragOver ? "border-primary bg-light" : ""}`}
                        style={{ minHeight: 80 }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOver(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOver(false);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOver(false);
                          const files = e.dataTransfer?.files;
                          if (files?.length) handleFileSelect(files);
                        }}
                      >
                        <Input
                          id="media-library-file-input-multi"
                          type="file"
                          multiple
                          accept=".jpg,.jpeg,.png,.gif,.webp,.mp4,.webm,.mov,.pdf,.doc,.docx"
                          onChange={(e) => handleFileSelect(e.target.files || [])}
                          className="mb-2"
                        />
                        <p className="small text-soft mb-0">
                          Or drag and drop multiple files here (max {MAX_UPLOAD_FILES})
                        </p>
                        {uploadFiles.length > 0 && (
                          <p className="small mb-0 mt-1">
                            {uploadFiles.length} file(s) selected
                          </p>
                        )}
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md="2">
                    <FormGroup>
                      <Label>Title (single file)</Label>
                      <Input
                        type="text"
                        placeholder="Title"
                        value={uploadTitle}
                        onChange={(e) => setUploadTitle(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="2">
                    <FormGroup>
                      <Label>Alt text (single file)</Label>
                      <Input
                        type="text"
                        placeholder="Alt"
                        value={uploadAlt}
                        onChange={(e) => setUploadAlt(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="2">
                    <FormGroup>
                      <Label>Usage</Label>
                      <Input
                        type="select"
                        value={uploadUsage}
                        onChange={(e) => setUploadUsage(e.target.value)}
                      >
                        {USAGE_OPTIONS.filter((o) => o.value).map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="2">
                    <FormGroup>
                      <Label>Folder (optional)</Label>
                      <Input
                        type="select"
                        value={uploadFolderId}
                        onChange={(e) => setUploadFolderId(e.target.value)}
                      >
                        <option value="">No folder</option>
                        {flatFolders.map((f) => (
                          <option key={f._id} value={f._id}>
                            {"\u00A0".repeat((f._depth || 0) * 2) + (f.name || "Unnamed")}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="2" className="d-flex align-items-end">
                    <Button
                      type="submit"
                      color="primary"
                      disabled={uploading || (uploadFiles.length === 0 && !uploadFile)}
                      className="w-100"
                    >
                      {uploading ? (
                        <Spinner size="sm" className="me-2" />
                      ) : null}
                      Upload
                    </Button>
                  </Col>
                </Row>
                <Row className="g-3 mt-1">
                  <Col md="4">
                    <p className="small text-soft mb-0">
                      Single file: use the file input above or select one file for title/alt. Multiple: select or drop several files; usage and folder apply to all.
                    </p>
                  </Col>
                </Row>
              </form>
            </Col>
          </Row>

          <Row>
            <Col md="3" className="mb-4">
              <div className="card card-bordered">
                <div className="card-inner">
                  <h6 className="card-title">Folders</h6>
                  {folderTreeLoading ? (
                    <div className="py-3 text-center">
                      <Spinner size="sm" color="primary" />
                    </div>
                  ) : folderTreeError ? (
                    <div className="mb-2">
                      <p className="text-danger small mb-1">{folderTreeError}</p>
                      <Button size="sm" color="light" outline onClick={() => fetchFolders()} aria-label="Retry loading folders">
                        Retry
                      </Button>
                    </div>
                  ) : null}
                  <nav aria-label="Media folders">
                    <div className="mb-2">
                      <div
                        className={`d-flex align-items-center py-1 px-2 rounded ${selectedFolderId === ALL_MEDIA_FOLDERS ? "bg-primary text-white" : ""}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => setSelectedFolderId(ALL_MEDIA_FOLDERS)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelectedFolderId(ALL_MEDIA_FOLDERS);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label="Show all media in any folder"
                      >
                        All media
                      </div>
                      <div
                        className={`d-flex align-items-center py-1 px-2 rounded mt-1 ${selectedFolderId === UNCategorized_ID ? "bg-primary text-white" : ""}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => setSelectedFolderId(UNCategorized_ID)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelectedFolderId(UNCategorized_ID);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label="Show uncategorized media"
                      >
                        Uncategorized
                      </div>
                    </div>
                    {!folderTreeLoading &&
                      folderTree.map((node) => renderFolderNode(node, 0))}
                  </nav>
                  <Button
                    color="primary"
                    size="sm"
                    outline
                    className="mt-2 w-100"
                    onClick={() => setNewFolderOpen(true)}
                    aria-label="Create new folder"
                  >
                    New folder
                  </Button>
                </div>
              </div>
            </Col>
            <Col md="9">
          <BlockHead className="mb-3">
            <BlockHeadContent>
              <BlockTitle tag="h5">Media list ({total} total)</BlockTitle>
            </BlockHeadContent>
          </BlockHead>

          <Row className="g-3 mb-4">
            <Col md="2">
              <FormGroup>
                <Label>Type</Label>
                <Input
                  type="select"
                  name="type"
                  value={filters.type}
                  onChange={handleFilterChange}
                >
                  {TYPE_OPTIONS.map((o) => (
                    <option key={o.value || "all"} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup>
                <Label>Usage</Label>
                <Input
                  type="select"
                  name="usage"
                  value={filters.usage}
                  onChange={handleFilterChange}
                >
                  {USAGE_OPTIONS.map((o) => (
                    <option key={o.value || "all"} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label>Owner</Label>
                <Input
                  type="select"
                  name="ownerId"
                  value={filters.ownerId}
                  onChange={handleFilterChange}
                >
                  <option value="">All owners</option>
                  {partners.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.organization_name || p._id}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : list.length === 0 ? (
            <p className="text-soft">No media found. Upload a file or adjust filters.</p>
          ) : (
            <>
              <Row className="g-3">
                {list.map((item) => {
                  const thumbUrl = thumbnailOrPreviewUrl(item);
                  return (
                    <Col key={item._id} xs="6" sm="6" md="4" lg="3">
                      <div className="card h-100">
                        <div
                          className="card-body p-2 d-flex flex-column"
                          style={{ minHeight: 140 }}
                        >
                          <div
                            className="d-flex align-items-center justify-content-center bg-light rounded mb-2"
                            style={{ height: 100, overflow: "hidden" }}
                          >
                            {thumbUrl ? (
                              <img
                                src={thumbUrl}
                                alt={item.alt || item.title || "Media"}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: 100,
                                  objectFit: "contain",
                                }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextSibling?.classList.remove("d-none");
                                }}
                              />
                            ) : null}
                            <span
                              className={
                                thumbUrl ? "d-none small text-muted" : "small text-muted"
                              }
                              aria-hidden
                            >
                              {item.type === "video"
                                ? "Video"
                                : item.type === "document"
                                ? "Document"
                                : "—"}
                            </span>
                          </div>
                          <div className="flex-grow-1 text-truncate small fw-medium" title={item.title || item.fileName || item.fileUrl}>
                            {item.title || item.fileName || item.fileUrl || "—"}
                          </div>
                          <div className="d-flex flex-wrap flex-g-1 mt-1">
                            <Badge
                              color={
                                item.type === "image"
                                  ? "primary"
                                  : item.type === "video"
                                  ? "info"
                                  : "secondary"
                              }
                              className="text-uppercase"
                            >
                              {item.type}
                            </Badge>
                            {item.usage === "logo" || item.usage === "press" ? (
                              <Badge color="success" className="ms-1 text-uppercase" title={item.cmsPlacement || ""}>
                                Logo
                              </Badge>
                            ) : null}
                            {item.usage === "press_logos" ? (
                              <Badge color="info" className="ms-1 text-uppercase">
                                Press
                              </Badge>
                            ) : null}
                          </div>
                          <div className="d-flex flex-g-1 mt-2">
                            <Button
                              size="sm"
                              color="primary"
                              outline
                              className="flex-grow-1"
                              onClick={() => openEditModal(item)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              color="danger"
                              outline
                              onClick={() => handleDelete(item._id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>

              {pages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span className="text-soft">
                    Page {page} of {pages} ({total} total)
                  </span>
                  <div>
                    <Button
                      size="sm"
                      outline
                      disabled={page <= 1}
                      onClick={() => fetchList(page - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      outline
                      className="ms-2"
                      disabled={page >= pages}
                      onClick={() => fetchList(page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
            </Col>
          </Row>
        </Block>

        {newFolderOpen && (
          <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} role="dialog" aria-modal="true" aria-labelledby="new-folder-title">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="new-folder-title">New folder</h5>
                  <button type="button" className="close" aria-label="Close" onClick={() => setNewFolderOpen(false)}>
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <form onSubmit={handleNewFolderSubmit}>
                  <div className="modal-body">
                    <FormGroup>
                      <Label htmlFor="new-folder-name">Folder name</Label>
                      <Input
                        id="new-folder-name"
                        type="text"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        placeholder="Folder name"
                        autoFocus
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="new-folder-parent">Parent folder (optional)</Label>
                      <Input
                        id="new-folder-parent"
                        type="select"
                        value={newFolderParentId}
                        onChange={(e) => setNewFolderParentId(e.target.value)}
                      >
                        <option value="">None (root)</option>
                        {flatFolders.map((f) => (
                          <option key={f._id} value={f._id}>
                            {"—".repeat(f._depth || 0)} {f.name || "Unnamed"}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </div>
                  <div className="modal-footer">
                    <Button type="button" color="light" onClick={() => setNewFolderOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" color="primary" disabled={newFolderSaving}>
                      {newFolderSaving ? <Spinner size="sm" className="me-2" /> : null}
                      Create
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {editFolderItem && (
          <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} role="dialog" aria-modal="true" aria-labelledby="edit-folder-title">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="edit-folder-title">Edit folder</h5>
                  <button type="button" className="close" aria-label="Close" onClick={closeEditFolder}>
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <form onSubmit={handleEditFolderSubmit}>
                  <div className="modal-body">
                    <FormGroup>
                      <Label htmlFor="edit-folder-name">Folder name</Label>
                      <Input
                        id="edit-folder-name"
                        type="text"
                        value={editFolderName}
                        onChange={(e) => setEditFolderName(e.target.value)}
                        placeholder="Folder name"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="edit-folder-parent">Parent folder</Label>
                      <Input
                        id="edit-folder-parent"
                        type="select"
                        value={editFolderParentId}
                        onChange={(e) => setEditFolderParentId(e.target.value)}
                      >
                        <option value="">None (root)</option>
                        {flatFolders
                          .filter(
                            (f) =>
                              f._id !== editFolderItem?._id &&
                              !getDescendantIds(editFolderItem).includes(String(f._id))
                          )
                          .map((f) => (
                            <option key={f._id} value={f._id}>
                              {"—".repeat(f._depth || 0)} {f.name || "Unnamed"}
                            </option>
                          ))}
                      </Input>
                    </FormGroup>
                  </div>
                  <div className="modal-footer">
                    <Button type="button" color="light" onClick={closeEditFolder}>
                      Cancel
                    </Button>
                    <Button type="submit" color="primary" disabled={editFolderSaving}>
                      {editFolderSaving ? <Spinner size="sm" className="me-2" /> : null}
                      Save
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {editItem && (
          <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} aria-modal="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit / Assign media</h5>
                  <button type="button" className="close" aria-label="Close" onClick={closeEditModal}>
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <form onSubmit={handleEditSubmit}>
                  <div className="modal-body">
                    <FormGroup>
                      <Label>Title</Label>
                      <Input
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
                        placeholder="Title"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Alt text</Label>
                      <Input
                        type="text"
                        value={editForm.alt}
                        onChange={(e) => setEditForm((p) => ({ ...p, alt: e.target.value }))}
                        placeholder="Alt (accessibility)"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Usage</Label>
                      <Input
                        type="select"
                        value={editForm.usage}
                        onChange={(e) => setEditForm((p) => ({ ...p, usage: e.target.value }))}
                      >
                        {USAGE_OPTIONS.filter((o) => o.value).map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Input>
                      {editForm.usage === "press_logos" ? (
                        <span className="small text-soft d-block mt-1">
                          Homepage press strip only (public list by usage). For other pages or keys, use
                          &quot;Logo strip (CMS)&quot; and set Placement key below.
                        </span>
                      ) : null}
                    </FormGroup>
                    {editForm.usage === "logo" && (
                      <>
                        <FormGroup>
                          <Label htmlFor="media-cms-placement">Placement key</Label>
                          <Input
                            id="media-cms-placement"
                            type="text"
                            placeholder="e.g. home_press, tourism_medical"
                            value={editForm.cmsPlacement}
                            onChange={(e) =>
                              setEditForm((p) => ({ ...p, cmsPlacement: e.target.value }))
                            }
                          />
                          <span className="small text-soft">
                            Same value as the <code>placement</code> prop on the site component.
                          </span>
                        </FormGroup>
                        <FormGroup check className="mb-3">
                          <Label check htmlFor="media-cms-visible">
                            <Input
                              type="checkbox"
                              id="media-cms-visible"
                              checked={editForm.cmsVisible}
                              onChange={(e) =>
                                setEditForm((p) => ({ ...p, cmsVisible: e.target.checked }))
                              }
                            />{" "}
                            Show in this strip
                          </Label>
                        </FormGroup>
                        <FormGroup>
                          <Label htmlFor="media-cms-order">Sort order</Label>
                          <Input
                            id="media-cms-order"
                            type="number"
                            min={0}
                            value={editForm.cmsOrder}
                            onChange={(e) =>
                              setEditForm((p) => ({
                                ...p,
                                cmsOrder: Number(e.target.value) || 0,
                              }))
                            }
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label htmlFor="media-cms-link">External link (optional)</Label>
                          <Input
                            id="media-cms-link"
                            type="url"
                            placeholder="https://"
                            value={editForm.cmsLink}
                            onChange={(e) => setEditForm((p) => ({ ...p, cmsLink: e.target.value }))}
                          />
                        </FormGroup>
                      </>
                    )}
                    <FormGroup>
                      <Label>Folder</Label>
                      <Input
                        type="select"
                        value={editForm.folderId === "" ? "none" : editForm.folderId}
                        onChange={(e) =>
                          setEditForm((p) => ({
                            ...p,
                            folderId: e.target.value === "none" ? "" : e.target.value,
                          }))
                        }
                      >
                        <option value="none">No folder</option>
                        {flatFolders.map((f) => (
                          <option key={f._id} value={f._id}>
                            {"\u00A0".repeat((f._depth || 0) * 2) + (f.name || "Unnamed")}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                    <FormGroup>
                      <Label>Attribution (owner)</Label>
                      <Input
                        type="select"
                        value={editForm.ownerType}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            ownerType: e.target.value,
                            ownerId: e.target.value === "partner" ? prev.ownerId : "",
                          }))
                        }
                      >
                        {OWNER_TYPE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                    {editForm.ownerType === "partner" && (
                      <FormGroup>
                        <Label>Partner</Label>
                        <Input
                          type="select"
                          value={editForm.ownerId}
                          onChange={(e) => setEditForm((p) => ({ ...p, ownerId: e.target.value }))}
                        >
                          <option value="">Select partner</option>
                          {partners.map((p) => (
                            <option key={p._id} value={p._id}>
                              {p.organization_name || p._id}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    )}
                  </div>
                  <div className="modal-footer">
                    <Button type="button" color="light" onClick={closeEditModal}>
                      Cancel
                    </Button>
                    <Button type="submit" color="primary" disabled={savingEdit}>
                      {savingEdit ? <Spinner size="sm" className="me-2" /> : null}
                      Save
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </Content>
    </>
  );
};

export default MediaLibrary;
